import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Modal,
    Button,
    Box,
    Text,
    Alert,
    Paper,
    ActionIcon,
    TextInput,
    Badge,
    Menu,
    Tabs,
    Select,
    Tooltip,
    Stack,
    Group
} from '@mantine/core';
import {
    IconGripVertical,
    IconPencil,
    IconTrash,
    IconPlus,
    IconArrowUp,
    IconArrowDown,
    IconChevronDown,
    IconAlertCircle
} from '@tabler/icons-react';
import { WorkflowStepEditDialog, EntityBrowserDialog } from '../dialogs';
import type { WorkflowStep } from './types';
import type { Workflow } from './types';
import { MODAL_SIZES } from '../../theme/mantineTheme';
import { entityService } from '../../services/entityServiceSingleton';
import { ConfigBuilder } from './SettingsBuilder';
import { JsonDiffViewer } from '../common/JsonDiffViewer';
import { JsonViewer } from '../common/JsonViewer';
import { DialogErrorAlert, EntityDialogActions } from '../dialogs';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { ShadcnUnsavedChangesDialog } from '../dialogs';
import { canonicalJsonString, getWorkflowConfigPayload, isJsonFieldReturnedAsString, JSON_FIELD_STRING_WARNING_MESSAGE } from '../../utils/workflowConfigUtils';
import EntitlementsService from '../../services/EntitlementsService';
import type { EntityEntitlementForTargetResponse } from '../../types/EntityEntitlements';
import { buildFormAttributesFromTargetEntitlements, buildFormAttributesFromTargetEntitlementsReadable } from '../../types/EntityEntitlements';
import { isA, createEntityRef } from '../../types/entity/EntityRef';
import { useViewModeSafe } from '../../contexts/ViewModeContext';

/** Step-level Settings (JSON object or string). */
function getStepSettingsValue(step: WorkflowStep | null | undefined): any {
    if (!step) return undefined;
    return step.Settings;
}

/** Workflow-level Settings (steps/zSteps). Returns JSON string. */
function getWorkflowSettingsString(w: { Settings?: any } | null | undefined): string {
    if (!w) return '{}';
    const raw = w.Settings;
    if (raw == null || raw === '') return '{}';
    return typeof raw === 'string' ? raw : JSON.stringify(raw);
}

interface WorkflowDesignerProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: (workflow: Workflow) => void;
    workflow?: Workflow | null;
    entityType?: string;
    stepEntityType?: string;
    presentation?: 'modal' | 'inline';
}

export const WorkflowDesigner: React.FC<WorkflowDesignerProps> = ({
    open,
    onClose,
    onSuccess,
    workflow,
    entityType = 'workflow',
    stepEntityType = 'WorkflowActivity',
    presentation = 'modal'
}) => {
    const { isStandard } = useViewModeSafe();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [steps, setSteps] = useState<WorkflowStep[]>([]);
    const [stepEditDialogOpen, setStepEditDialogOpen] = useState(false);
    const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null);
    const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [entityBrowserOpen, setEntityBrowserOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('0');

    const [workflowData, setWorkflowData] = useState<Workflow>({
        DisplayName: '',
        Steps: [],
        Settings: '{}'
    });

    /** Tier3 entitlement for this workflow (edit). Drives which fields exist; API often omits empty/null, so we need tier3 for generic inputs. */
    const [targetEntitlement, setTargetEntitlement] = useState<EntityEntitlementForTargetResponse | null>(null);
    const [tier3Settled, setTier3Settled] = useState(false);
    const [tier3Error, setTier3Error] = useState<string | null>(null);

    const isEditMode = !!workflow?.Id;
    const lastFetchedWorkflowIdRef = useRef<string | null>(null);
    /** Settings string from the last full entity load (getEntityById). Used as baseline for change detection so we don't compare against the list-item prop. */
    const lastLoadedSettingsRef = useRef<string | null>(null);

    /** Reserved workflow attributes (Id, EntityType, DisplayName, Type, Settings, Steps) — use AttributeRef match so we don't show duplicates or editable Id/EntityType. */
    const WORKFLOW_RESERVED_ATTRIBUTE_REFS = useMemo(
        () => ['Id', 'EntityType', 'DisplayName', 'Type', 'Settings', 'Steps'].map(createEntityRef),
        []
    );

    /** Reserved step keys for filtering when merging step objects (matched via isA for robust id/displayName). */
    const WORKFLOW_STEP_RESERVED_REFS = useMemo(
        () => ['Id', 'DisplayName', 'Description', 'Settings', 'Script', 'Action', 'Authorization', 'EntityType'].map(createEntityRef),
        []
    );

    /** Tier3-driven form attributes for generic inputs. Exclude reserved (matched via attributeRefEquals). */
    const tier3FormAttributes = useMemo(() => {
        const fromSet = buildFormAttributesFromTargetEntitlements(targetEntitlement);
        const list = fromSet.length > 0 ? fromSet : buildFormAttributesFromTargetEntitlementsReadable(targetEntitlement);
        return list.filter((a) => {
            try {
                const aRef = createEntityRef(a);
                return !WORKFLOW_RESERVED_ATTRIBUTE_REFS.some((r) => isA(r, aRef.id));
            } catch {
                return false;
            }
        });
    }, [targetEntitlement, WORKFLOW_RESERVED_ATTRIBUTE_REFS]);

    // Extract step aliases from config for display
    const stepAliases = useMemo(() => {
        const aliases = new Map<string, string>();
        try {
            const config = workflowData.Settings || '{}';
            if (config.trim() !== '' && config.trim() !== '{}') {
                const configObj = JSON.parse(config);
                if (configObj.steps && Array.isArray(configObj.steps)) {
                    configObj.steps.forEach((stepObj: any) => {
                        if (stepObj.stepId && stepObj.stepAlias) {
                            aliases.set(stepObj.stepId, stepObj.stepAlias);
                        }
                    });
                }
                // Legacy support: also check stepAliases object
                if (configObj.stepAliases && typeof configObj.stepAliases === 'object') {
                    Object.entries(configObj.stepAliases).forEach(([stepId, alias]) => {
                        if (!aliases.has(stepId) && alias) {
                            aliases.set(stepId, alias as string);
                        }
                    });
                }
            }
        } catch (e) {
            // Invalid JSON, ignore
        }
        return aliases;
    }, [workflowData.Settings]);

    // Load workflow data: in edit mode always fetch full workflow so Settings (steps/zSteps) is present
    useEffect(() => {
        if (!open) {
            lastFetchedWorkflowIdRef.current = null;
            lastLoadedSettingsRef.current = null;
            setError(null);
            return;
        }
        const workflowId = workflow?.Id;
        if (workflowId) {
            // Prevent duplicate fetch when effect runs twice (e.g. Strict Mode) or deps churn
            if (lastFetchedWorkflowIdRef.current === workflowId) return;
            lastFetchedWorkflowIdRef.current = workflowId;
            lastLoadedSettingsRef.current = null;
            setLoading(true);
            setError(null);
            const typeStr = typeof entityType === 'string' ? entityType : 'workflow';
            entityService.getEntityById(workflowId, typeStr, false)
                .then((full) => {
                    const settingsStr = getWorkflowSettingsString(full);
                    lastLoadedSettingsRef.current = settingsStr;
                    setWorkflowData({
                        ...full,
                        DisplayName: full.DisplayName || '',
                        Steps: [],
                        Settings: settingsStr,
                        Type: full.Type || ''
                    });
                    let stepRefs: (WorkflowStep | string | any)[] = [];
                    try {
                        if (settingsStr.trim() !== '' && settingsStr.trim() !== '{}') {
                            const configObj = JSON.parse(settingsStr);
                            if (configObj.steps && Array.isArray(configObj.steps)) {
                                stepRefs = configObj.steps;
                            }
                        }
                    } catch {
                        stepRefs = full.Steps || [];
                    }
                    loadSteps(stepRefs, settingsStr);
                })
                .catch((err) => {
                    lastFetchedWorkflowIdRef.current = null;
                    lastLoadedSettingsRef.current = null;
                    console.error('Failed to load workflow:', err);
                    setError(err?.message || 'Failed to load workflow');
                    setWorkflowData({
                        DisplayName: workflow.DisplayName || '',
                        Steps: [],
                        Settings: getWorkflowSettingsString(workflow),
                        Type: workflow.Type || ''
                    });
                    setSteps([]);
                })
                .finally(() => setLoading(false));
        } else if (workflow) {
            const settingsStr = getWorkflowSettingsString(workflow);
            setWorkflowData({
                DisplayName: workflow.DisplayName || '',
                Steps: [],
                Settings: settingsStr,
                Type: workflow.Type || ''
            });
            let stepRefs: (WorkflowStep | string | any)[] = [];
            try {
                if (settingsStr.trim() !== '' && settingsStr.trim() !== '{}') {
                    const configObj = JSON.parse(settingsStr);
                    if (configObj.steps && Array.isArray(configObj.steps)) stepRefs = configObj.steps;
                }
            } catch {
                stepRefs = workflow.Steps || [];
            }
            loadSteps(stepRefs, settingsStr);
        } else {
            setWorkflowData({
                DisplayName: '',
                Steps: [],
                Settings: '{}',
                Type: ''
            });
            setSteps([]);
        }
        setError(null);
    }, [open, workflow?.Id, entityType, entityService]);

    // Tier3 entitlement for edit (GET .../Entitlements/entity/Workflow/{id}). Required: drives which fields exist; API omits empty arrays/strings/null so tier3 defines generic inputs.
    useEffect(() => {
        if (!open) {
            setTargetEntitlement(null);
            setTier3Settled(false);
            setTier3Error(null);
            return;
        }
        if (!workflow?.Id) {
            setTargetEntitlement(null);
            setTier3Settled(true);
            setTier3Error(null);
            return;
        }
        const schemaEntityId = typeof entityType === 'string' ? (entityType.charAt(0).toUpperCase() + entityType.slice(1).toLowerCase()) : 'Workflow';
        setTier3Settled(false);
        setTier3Error(null);
        let cancelled = false;
        EntitlementsService.fetchEntityEntitlementForTarget(schemaEntityId, workflow.Id)
            .then((result) => {
                if (!cancelled) {
                    setTargetEntitlement(result ?? null);
                    setTier3Error(null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    const msg = err instanceof Error ? err.message : String(err);
                    console.error(`WorkflowDesigner: tier3 entitlement failed for ${schemaEntityId}/${workflow.Id}:`, err);
                    setTargetEntitlement(null);
                    setTier3Error(msg || 'Failed to load edit permissions');
                }
            })
            .finally(() => {
                if (!cancelled) setTier3Settled(true);
            });
        return () => { cancelled = true; };
    }, [open, workflow?.Id, entityType]);

    // Load full step objects from config steps array (new format) or legacy format
    // configOverride: when provided (e.g. from initial fetch), use it so we don't rely on stale workflow/workflowData
    const loadSteps = async (stepRefs: (WorkflowStep | string | any)[], configOverride?: string) => {
        try {
            const loadedSteps: WorkflowStep[] = [];
            const configSource = configOverride ?? getWorkflowSettingsString(workflow) ?? (typeof workflowData.Settings === 'string' ? workflowData.Settings : workflowData.Settings != null ? JSON.stringify(workflowData.Settings) : '') ?? '{}';
            let configObj: any = {};
            try {
                if (configSource && configSource.trim() !== '' && configSource.trim() !== '{}') {
                    configObj = JSON.parse(configSource);
                }
            } catch (e) {
                // Invalid JSON
            }

            const zSteps = configObj.zSteps || {};

            for (const stepRef of stepRefs) {
                if (typeof stepRef === 'string') {
                    // Legacy format: it's just an ID string
                    // Try to load from zSteps first, then fetch if not found
                    if (zSteps[stepRef]) {
                        loadedSteps.push(zSteps[stepRef] as WorkflowStep);
                    } else {
                        try {
                            const step = await entityService.getEntityById(stepRef, stepEntityType.toLowerCase());
                            loadedSteps.push(step);
                        } catch (err) {
                            console.error(`Failed to load step ${stepRef}:`, err);
                            // Add placeholder for missing step
                            loadedSteps.push({
                                Id: stepRef,
                                DisplayName: `[Step ${stepRef}]`,
                                Description: 'Step not found',
                                Settings: '{}',
                                Script: ''
                            });
                        }
                    }
                } else if (stepRef && typeof stepRef === 'object') {
                    if (stepRef.stepId) {
                        // New format: stepId, stepAlias, valueOverrides
                        const stepId = stepRef.stepId;
                        // Try to load from zSteps first
                        if (zSteps[stepId]) {
                            loadedSteps.push(zSteps[stepId] as WorkflowStep);
                        } else {
                            // Fetch from API if not in zSteps
                            try {
                                const step = await entityService.getEntityById(stepId, stepEntityType.toLowerCase());
                                loadedSteps.push(step);
                            } catch (err) {
                                console.error(`Failed to load step ${stepId}:`, err);
                                loadedSteps.push({
                                    Id: stepId,
                                    DisplayName: `[Step ${stepId}]`,
                                    Description: 'Step not found',
                                    Settings: '{}',
                                    Script: ''
                                });
                            }
                        }
                    } else if (stepRef.id) {
                        // Legacy format: it's a step object with id, config, script, version, alias
                        const stepId = stepRef.id;
                        // Try to load from zSteps first
                        if (zSteps[stepId]) {
                            loadedSteps.push(zSteps[stepId] as WorkflowStep);
                        } else {
                            try {
                                const step = await entityService.getEntityById(stepId, stepEntityType.toLowerCase());
                                // Override with lifted config/script if present
                                if (stepRef.config !== undefined) {
                                    let configValue = typeof stepRef.config === 'string' ? stepRef.config : JSON.stringify(stepRef.config);
                                    step.Settings = configValue;
                                }
                                if (stepRef.script !== undefined) {
                                    step.Script = stepRef.script || '';
                                }
                                loadedSteps.push(step);
                            } catch (err) {
                                console.error(`Failed to load step ${stepId}:`, err);
                                loadedSteps.push({
                                    Id: stepId,
                                    DisplayName: `[Step ${stepId}]`,
                                    Description: 'Step not found',
                                    Settings: typeof stepRef.config === 'string' ? stepRef.config : JSON.stringify(stepRef.config || {}),
                                    Script: stepRef.script || ''
                                });
                            }
                        }
                    } else if (stepRef.Id) {
                        // It's already a full step object
                        loadedSteps.push(stepRef);
                    }
                }
            }

            setSteps(loadedSteps);
            // Initialize Settings.steps and zSteps when loading existing workflow
            if (loadedSteps.length > 0) {
                updateConfigSteps(loadedSteps, configObj);
            }
        } catch (err) {
            console.error('Error loading steps:', err);
            setError('Failed to load workflow steps');
        }
    };

    const handleCreateNewStep = () => {
        setEditingStep(null);
        setEditingStepIndex(null);
        setStepEditDialogOpen(true);
    };

    const handleAddExistingStep = () => {
        setEntityBrowserOpen(true);
    };

    const handleExistingStepSelected = async (selectedEntities: Array<{ Id: string; DisplayName: string; EntityType: string }>) => {
        if (selectedEntities.length === 0) {
            setEntityBrowserOpen(false);
            return;
        }

        try {
            const selectedIds = selectedEntities.map((e) => e.Id);
            // Load the selected step entities (full step data needed for workflow)
            const selectedSteps: WorkflowStep[] = [];
            for (const stepId of selectedIds) {
                try {
                    const step = await entityService.getEntityById(stepId, stepEntityType.toLowerCase());
                    selectedSteps.push(step);
                } catch (err) {
                    console.error(`Failed to load step ${stepId}:`, err);
                }
            }

            // Add the selected steps to the workflow (avoid duplicates)
            const existingStepIds = new Set(steps.map(s => s.Id).filter(Boolean));
            const newSteps = selectedSteps.filter(step => step.Id && !existingStepIds.has(step.Id));
            const updatedSteps = [...steps, ...newSteps];
            setSteps(updatedSteps);
            updateConfigSteps(updatedSteps);
            setEntityBrowserOpen(false);
        } catch (err) {
            console.error('Error loading selected steps:', err);
            setError('Failed to load selected steps');
        }
    };

    const handleEditStep = (index: number) => {
        const step = steps[index];
        setEditingStep(step);
        setEditingStepIndex(index);

        // Get stored copy from zSteps for comparison
        try {
            const config = workflowData.Settings || '{}';
            const configObj = config.trim() === '' || config.trim() === '{}' ? {} : JSON.parse(config);
            const zSteps = configObj.zSteps || {};
            const storedCopy = step?.Id ? zSteps[step.Id] : null;
            setEditingStep(storedCopy ? { ...step, _storedCopy: storedCopy } : step);
        } catch (e) {
            // If config parsing fails, just use the step
        }

        setStepEditDialogOpen(true);
    };

    // Update Settings.steps and zSteps when steps change
    // New format: steps array contains only stepId, stepAlias, valueOverrides
    // zSteps contains full copies of workflow activities
    const updateConfigSteps = (newSteps: WorkflowStep[], existingConfigObj?: any) => {
        try {
            // Use functional update to avoid stale state
            setWorkflowData(prev => {
                const currentConfig = prev.Settings || '{}';
                const configObj = existingConfigObj || (currentConfig.trim() === '' || currentConfig.trim() === '{}'
                    ? {}
                    : JSON.parse(currentConfig));

                // Get step aliases from config (legacy support)
                const stepAliases = configObj.stepAliases || {};

                // Get existing zSteps to preserve copies for steps that aren't changing
                const existingZSteps = configObj.zSteps || {};

                // Build steps array with simplified format: stepId, stepAlias, valueOverrides
                const stepsArray = newSteps
                    .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                    .map(step => {
                        const stepObj: any = {
                            stepId: step.Id
                        };

                        // Include alias (from stepAliases or from existing step object)
                        const alias = stepAliases[step.Id] || (configObj.steps && Array.isArray(configObj.steps)
                            ? configObj.steps.find((s: any) => s.stepId === step.Id || s.id === step.Id)?.stepAlias
                            : undefined);
                        if (alias) {
                            stepObj.stepAlias = alias;
                        }

                        // Include valueOverrides (from existing step object if present)
                        const existingStep = configObj.steps && Array.isArray(configObj.steps)
                            ? configObj.steps.find((s: any) => s.stepId === step.Id || s.id === step.Id)
                            : undefined;
                        if (existingStep && existingStep.valueOverrides) {
                            stepObj.valueOverrides = existingStep.valueOverrides;
                        }

                        return stepObj;
                    });

                // Store full copies of workflow activities in zSteps
                const zSteps: any = { ...existingZSteps };
                newSteps
                    .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                    .forEach(step => {
                        // Create a deep copy of the step
                        zSteps[step.Id] = {
                            Id: step.Id,
                            DisplayName: step.DisplayName,
                            Description: step.Description,
                            Settings: getStepSettingsValue(step) ?? (typeof step.Settings === 'string' ? step.Settings : JSON.stringify(step.Settings || {})),
                            Script: step.Script,
                            Action: step.Action,
                            Authorization: step.Authorization,
                            EntityType: step.EntityType,
                            // Include any other properties
                            ...Object.fromEntries(
                                Object.entries(step).filter(([key]) =>
                                    !WORKFLOW_STEP_RESERVED_REFS.some((r) => isA(r, key))
                                )
                            )
                        };
                    });

                // Update steps and zSteps in config
                configObj.steps = stepsArray;
                configObj.zSteps = zSteps;

                const newConfig = JSON.stringify(configObj, null, 2);
                return { ...prev, Settings: newConfig };
            });
        } catch (e) {
            console.error('Error updating config steps:', e);
        }
    };

    const handleDeleteStep = (index: number) => {
        const newSteps = steps.filter((_, i) => i !== index);
        setSteps(newSteps);
        updateConfigSteps(newSteps);
    };

    const handleStepSave = (savedStep: WorkflowStep) => {
        let newSteps: WorkflowStep[];
        if (editingStepIndex !== null) {
            // Update existing step
            newSteps = [...steps];
            newSteps[editingStepIndex] = savedStep;
        } else {
            // Add new step
            newSteps = [...steps, savedStep];
        }
        setSteps(newSteps);
        setStepEditDialogOpen(false);
        setEditingStep(null);
        setEditingStepIndex(null);

        // Update Settings.steps when steps change
        updateConfigSteps(newSteps);
    };

    const handleMoveStep = (index: number, direction: 'up' | 'down') => {
        const newSteps = [...steps];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex >= 0 && targetIndex < newSteps.length) {
            [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
            setSteps(newSteps);
            updateConfigSteps(newSteps);
        }
    };

    // Drag and drop handlers
    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex !== null && draggedIndex !== dropIndex) {
            const newSteps = [...steps];
            const draggedStep = newSteps[draggedIndex];

            newSteps.splice(draggedIndex, 1);
            newSteps.splice(dropIndex, 0, draggedStep);

            setSteps(newSteps);
            updateConfigSteps(newSteps);
        }

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleSave = async () => {
        if (!workflowData.DisplayName) {
            setError('Display Name is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Save steps first if they have changes
            const stepIds: string[] = [];
            for (const step of steps) {
                if (step.Id) {
                    stepIds.push(step.Id);
                } else {
                    // Step needs to be created first
                    setError('All steps must be saved before saving the workflow');
                    setLoading(false);
                    return;
                }
            }

            // Get the current config from workflowData (which may have been updated by WorkflowConfigEditor)
            let finalConfig = workflowData.Settings || '{}';

            // Ensure zSteps are up to date (but preserve the config property that WorkflowConfigEditor built)
            // Don't call updateConfigSteps here as it would overwrite the config property
            // Instead, just ensure zSteps contains current copies of all steps

            // Ensure finalConfig has the correct structure with steps and zSteps
            try {
                const configObj = finalConfig.trim() === '' || finalConfig.trim() === '{}'
                    ? {}
                    : JSON.parse(finalConfig);

                // Ensure zSteps contains current copies of all steps
                const zSteps: any = configObj.zSteps || {};
                steps
                    .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                    .forEach(step => {
                        zSteps[step.Id] = {
                            Id: step.Id,
                            DisplayName: step.DisplayName,
                            Description: step.Description,
                            Settings: getStepSettingsValue(step) ?? (typeof step.Settings === 'string' ? step.Settings : JSON.stringify(step.Settings || {})),
                            Script: step.Script,
                            Action: step.Action,
                            Authorization: step.Authorization,
                            EntityType: step.EntityType,
                            ...Object.fromEntries(
                                Object.entries(step).filter(([key]) =>
                                    !WORKFLOW_STEP_RESERVED_REFS.some((r) => isA(r, key))
                                )
                            )
                        };
                    });

                configObj.zSteps = zSteps;

                // Preserve the steps array from WorkflowConfigEditor if it exists and has config properties
                // Otherwise, build it from scratch
                if (configObj.steps && Array.isArray(configObj.steps) && configObj.steps.length > 0) {
                    // Check if any step has a config property (indicating WorkflowConfigEditor built it)
                    const hasConfigProperties = configObj.steps.some((s: any) => s.config && Object.keys(s.config).length > 0);

                    if (hasConfigProperties) {
                        // WorkflowConfigEditor has already built the steps with config properties
                        // Just ensure all current steps are present and zSteps are up to date
                        const existingStepIds = new Set(configObj.steps.map((s: any) => s.stepId || s.id));
                        steps
                            .filter((step): step is WorkflowStep & { Id: string } => !!step.Id && !existingStepIds.has(step.Id))
                            .forEach(step => {
                                configObj.steps.push({ stepId: step.Id });
                            });
                    } else {
                        // No config properties, need to build them
                        const getDataTypeFromControlType = (controlType: string): string => {
                            const lowerType = (controlType || '').toLowerCase();
                            if (lowerType.includes('number') || lowerType.includes('integer') || lowerType.includes('numeric')) {
                                return 'number';
                            }
                            if (lowerType.includes('boolean') || lowerType === 'checkbox') {
                                return 'boolean';
                            }
                            if (lowerType.includes('array') || lowerType.includes('multi')) {
                                return 'array';
                            }
                            if (lowerType.includes('object') || lowerType.includes('json')) {
                                return 'object';
                            }
                            return 'string';
                        };

                        const stepAliases = configObj.stepAliases || {};
                        const stepsArray = steps
                            .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                            .map(step => {
                                const stepObj: any = { stepId: step.Id };

                                const existingStep = configObj.steps.find((s: any) => s.stepId === step.Id || s.id === step.Id);
                                if (existingStep) {
                                    if (existingStep.stepAlias) stepObj.stepAlias = existingStep.stepAlias;
                                    if (existingStep.valueOverrides) stepObj.valueOverrides = existingStep.valueOverrides;
                                } else if (stepAliases[step.Id]) {
                                    stepObj.stepAlias = stepAliases[step.Id];
                                }

                                const valueOverrides = stepObj.valueOverrides || {};
                                const fullStep = zSteps[step.Id];
                                const config: any = {};

                                const stepSettingsRaw = fullStep && getStepSettingsValue(fullStep);
                                if (stepSettingsRaw) {
                                    try {
                                        const stepConfig = typeof stepSettingsRaw === 'string'
                                            ? JSON.parse(stepSettingsRaw)
                                            : stepSettingsRaw;

                                        if (stepConfig.props && Array.isArray(stepConfig.props) && stepConfig.props.length > 0) {
                                            stepConfig.props.forEach((prop: any) => {
                                                const propName = prop.name;
                                                const hasWorkflowOverride = valueOverrides && valueOverrides.hasOwnProperty(propName);
                                                const defaultValue = prop.defaultValue !== undefined ? prop.defaultValue : null;
                                                const workflowValue = hasWorkflowOverride ? valueOverrides[propName] : undefined;
                                                const value = hasWorkflowOverride ? workflowValue : defaultValue;
                                                const dataType = getDataTypeFromControlType(prop.controlType);

                                                const configEntry: any = { value: value, type: dataType };

                                                if (hasWorkflowOverride && workflowValue !== undefined) {
                                                    configEntry.workflowValue = workflowValue;
                                                }

                                                if (prop.entityType) {
                                                    const entityTypeValue = prop.entityType;
                                                    configEntry.entityType = typeof entityTypeValue === 'object' && entityTypeValue !== null
                                                        ? ((entityTypeValue as any).EntityType || (entityTypeValue as any).Id || (entityTypeValue as any).DisplayName || String(entityTypeValue))
                                                        : String(entityTypeValue);
                                                    configEntry.isReference = true;
                                                }

                                                config[propName] = configEntry;
                                            });
                                        }
                                    } catch (e) {
                                        // Invalid JSON, skip
                                    }
                                }

                                if (Object.keys(config).length > 0) {
                                    stepObj.config = config;
                                }

                                return stepObj;
                            });

                        configObj.steps = stepsArray;
                    }
                } else {
                    // No steps array, build it from scratch
                    const getDataTypeFromControlType = (controlType: string): string => {
                        const lowerType = (controlType || '').toLowerCase();
                        if (lowerType.includes('number') || lowerType.includes('integer') || lowerType.includes('numeric')) {
                            return 'number';
                        }
                        if (lowerType.includes('boolean') || lowerType === 'checkbox') {
                            return 'boolean';
                        }
                        if (lowerType.includes('array') || lowerType.includes('multi')) {
                            return 'array';
                        }
                        if (lowerType.includes('object') || lowerType.includes('json')) {
                            return 'object';
                        }
                        return 'string';
                    };

                    const stepAliases = configObj.stepAliases || {};
                    const stepsArray = steps
                        .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                        .map(step => {
                            const stepObj: any = { stepId: step.Id };

                            if (stepAliases[step.Id]) {
                                stepObj.stepAlias = stepAliases[step.Id];
                            }

                            const fullStep = zSteps[step.Id];
                            const config: any = {};

                            const stepSettingsRaw = fullStep && getStepSettingsValue(fullStep);
                            if (stepSettingsRaw) {
                                try {
                                    const stepConfig = typeof stepSettingsRaw === 'string'
                                        ? JSON.parse(stepSettingsRaw)
                                        : stepSettingsRaw;

                                    if (stepConfig.props && Array.isArray(stepConfig.props) && stepConfig.props.length > 0) {
                                        stepConfig.props.forEach((prop: any) => {
                                            const propName = prop.name;
                                            const defaultValue = prop.defaultValue !== undefined ? prop.defaultValue : null;
                                            const dataType = getDataTypeFromControlType(prop.controlType);

                                            const configEntry: any = { value: defaultValue, type: dataType };

                                            if (prop.entityType) {
                                                const entityTypeValue = prop.entityType;
                                                configEntry.entityType = typeof entityTypeValue === 'object' && entityTypeValue !== null
                                                    ? ((entityTypeValue as any).EntityType || (entityTypeValue as any).Id || (entityTypeValue as any).DisplayName || String(entityTypeValue))
                                                    : String(entityTypeValue);
                                                configEntry.isReference = true;
                                            }

                                            config[propName] = configEntry;
                                        });
                                    }
                                } catch (e) {
                                    // Invalid JSON, skip
                                }
                            }

                            if (Object.keys(config).length > 0) {
                                stepObj.config = config;
                            }

                            return stepObj;
                        });

                    configObj.steps = stepsArray;
                }

                finalConfig = JSON.stringify(configObj, null, 2);
            } catch (e) {
                // If config is invalid, create new one
                const zSteps: any = {};
                steps
                    .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                    .forEach(step => {
                        zSteps[step.Id] = {
                            Id: step.Id,
                            DisplayName: step.DisplayName,
                            Description: step.Description,
                            Settings: getStepSettingsValue(step) ?? (typeof step.Settings === 'string' ? step.Settings : JSON.stringify(step.Settings || {})),
                            Script: step.Script,
                            Action: step.Action,
                            Authorization: step.Authorization,
                            EntityType: step.EntityType
                        };
                    });

                finalConfig = JSON.stringify({
                    steps: steps
                        .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                        .map(step => ({ stepId: step.Id })),
                    zSteps: zSteps
                }, null, 2);
            }

            let savedWorkflow: Workflow;

            const operations: any[] = [];

            // Only add operations for changed fields
            if (isEditMode && workflow?.Id) {
                if (workflow.DisplayName !== workflowData.DisplayName) {
                    operations.push({ op: 'replace', path: 'DisplayName', value: workflowData.DisplayName });
                }

                if (workflow.Type !== workflowData.Type) {
                    operations.push({ op: 'replace', path: 'Type', value: workflowData.Type });
                }

                // Compare Settings (canonical JSON so key order doesn't falsely trigger replace)
                const configPayload = getWorkflowConfigPayload(finalConfig);
                const normalizedOldConfig = canonicalJsonString(getWorkflowSettingsString(workflow));
                const normalizedNewConfig = canonicalJsonString(configPayload);
                if (normalizedOldConfig !== normalizedNewConfig) {
                    operations.push({ op: 'replace', path: 'Settings', value: configPayload });
                }

                if (operations.length > 0) {
                    savedWorkflow = await entityService.patchEntity(workflow.Id, {
                        Operations: operations
                    }, entityType.toLowerCase());
                } else {
                    savedWorkflow = workflow;
                }
            } else {
                // Create new workflow - steps and zSteps go in Settings
                // Only send DisplayName, Type, and Settings (not Steps at top level)
                const createData: any = {
                    DisplayName: workflowData.DisplayName
                };

                if (workflowData.Type) {
                    createData.Type = workflowData.Type;
                }

                // Only include Settings if it has content (steps or zSteps); send as object, not string
                if (finalConfig && finalConfig.trim() !== '' && finalConfig.trim() !== '{}') {
                    try {
                        const configPayload = getWorkflowConfigPayload(finalConfig);
                        if ((configPayload.steps && Array.isArray(configPayload.steps) && configPayload.steps.length > 0) ||
                            (configPayload.zSteps && Object.keys(configPayload.zSteps).length > 0)) {
                            createData.Settings = configPayload;
                        }
                    } catch (e) {
                        // If config is invalid, don't include it
                    }
                }

                savedWorkflow = await entityService.createEntity(entityType.toLowerCase(), createData);
            }

            if (onSuccess) {
                onSuccess(savedWorkflow);
            }
            onClose();
        } catch (err) {
            console.error('Error saving workflow:', err);
            setError(err instanceof Error ? err.message : 'Failed to save workflow');
        } finally {
            setLoading(false);
        }
    };

    // Compute original and current data for diff viewer (edit mode only)
    const { originalData, currentData, expectedPayload, hasChanges: workflowHasChanges } = useMemo(() => {
        if (!isEditMode || !workflow) {
            // For create mode, compute expected payload
            // Build final config similar to handleSave
            let finalConfig = workflowData.Settings || '{}';
            try {
                const configObj = finalConfig.trim() === '' || finalConfig.trim() === '{}'
                    ? {}
                    : JSON.parse(finalConfig);

                // Ensure zSteps contains current copies of all steps
                const zSteps: any = configObj.zSteps || {};
                steps
                    .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                    .forEach(step => {
                        zSteps[step.Id] = {
                            Id: step.Id,
                            DisplayName: step.DisplayName,
                            Description: step.Description,
                            Settings: getStepSettingsValue(step) ?? (typeof step.Settings === 'string' ? step.Settings : JSON.stringify(step.Settings || {})),
                            Script: step.Script,
                            Action: step.Action,
                            Authorization: step.Authorization,
                            EntityType: step.EntityType
                        };
                    });
                configObj.zSteps = zSteps;

                // Helper function to map controlType to data type
                const getDataTypeFromControlType = (controlType: string): string => {
                    const lowerType = (controlType || '').toLowerCase();
                    if (lowerType.includes('number') || lowerType.includes('integer') || lowerType.includes('numeric')) {
                        return 'number';
                    }
                    if (lowerType.includes('boolean') || lowerType === 'checkbox') {
                        return 'boolean';
                    }
                    if (lowerType.includes('array') || lowerType.includes('multi')) {
                        return 'array';
                    }
                    if (lowerType.includes('object') || lowerType.includes('json')) {
                        return 'object';
                    }
                    // Default to string
                    return 'string';
                };

                // Get stepAliases and valueOverrides from existing config
                const stepAliases = configObj.stepAliases || {};
                const existingSteps = configObj.steps || [];

                // Ensure steps array is in the correct format: stepId, stepAlias, valueOverrides, config
                const stepsArray = steps
                    .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                    .map(step => {
                        const stepObj: any = { stepId: step.Id };

                        // Get alias from existing config
                        const existingStep = existingSteps.find((s: any) => s.stepId === step.Id || s.id === step.Id);
                        if (existingStep && existingStep.stepAlias) {
                            stepObj.stepAlias = existingStep.stepAlias;
                        } else if (stepAliases[step.Id]) {
                            stepObj.stepAlias = stepAliases[step.Id];
                        }

                        // Get valueOverrides from existing config: only include properties that are actually overridden
                        const valueOverrides = existingStep && existingStep.valueOverrides
                            ? existingStep.valueOverrides
                            : {};

                        // Only include valueOverrides if there are actual overrides
                        if (Object.keys(valueOverrides).length > 0) {
                            stepObj.valueOverrides = valueOverrides;
                        }

                        // Build config property: lift props from zSteps with values, types, and workflow overrides
                        // ALWAYS build this for ALL steps, even if they don't have props
                        const fullStep = zSteps[step.Id];
                        const config: any = {};

                        const fullStepSettingsVal = fullStep && getStepSettingsValue(fullStep);
                        if (fullStepSettingsVal) {
                            try {
                                const stepConfig = typeof fullStepSettingsVal === 'string'
                                    ? JSON.parse(fullStepSettingsVal)
                                    : fullStepSettingsVal;

                                if (stepConfig.props && Array.isArray(stepConfig.props) && stepConfig.props.length > 0) {
                                    stepConfig.props.forEach((prop: any) => {
                                        const propName = prop.name;
                                        const hasWorkflowOverride = valueOverrides && valueOverrides.hasOwnProperty(propName);
                                        const defaultValue = prop.defaultValue !== undefined ? prop.defaultValue : null;
                                        const workflowValue = hasWorkflowOverride ? valueOverrides[propName] : undefined;
                                        const value = hasWorkflowOverride ? workflowValue : defaultValue;
                                        const dataType = getDataTypeFromControlType(prop.controlType);

                                        const configEntry: any = {
                                            value: value,
                                            type: dataType
                                        };

                                        // Only include workflowValue if there's an override
                                        if (hasWorkflowOverride && workflowValue !== undefined) {
                                            configEntry.workflowValue = workflowValue;
                                        }

                                        // Include entityType and isReference if this prop is an entity selector
                                        if (prop.entityType) {
                                            const entityTypeValue = prop.entityType;
                                            configEntry.entityType = typeof entityTypeValue === 'object' && entityTypeValue !== null
                                                ? ((entityTypeValue as any).EntityType || (entityTypeValue as any).Id || (entityTypeValue as any).DisplayName || String(entityTypeValue))
                                                : String(entityTypeValue);
                                            configEntry.isReference = true;
                                        }

                                        config[propName] = configEntry;
                                    });
                                }
                            } catch (e) {
                                // Invalid JSON, skip config building
                            }
                        }

                        // Always include config property if it has entries
                        if (Object.keys(config).length > 0) {
                            stepObj.config = config;
                        }

                        return stepObj;
                    });
                configObj.steps = stepsArray;

                finalConfig = JSON.stringify(configObj, null, 2);
            } catch (e) {
                // If config is invalid, create new one
                const zSteps: any = {};
                steps
                    .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                    .forEach(step => {
                        zSteps[step.Id] = {
                            Id: step.Id,
                            DisplayName: step.DisplayName,
                            Description: step.Description,
                            Settings: getStepSettingsValue(step) ?? (typeof step.Settings === 'string' ? step.Settings : JSON.stringify(step.Settings || {})),
                            Script: step.Script,
                            Action: step.Action,
                            Authorization: step.Authorization,
                            EntityType: step.EntityType
                        };
                    });
                finalConfig = JSON.stringify({
                    steps: steps
                        .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                        .map(step => ({ stepId: step.Id })),
                    zSteps: zSteps
                }, null, 2);
            }

            const createPayload: any = {
                DisplayName: workflowData.DisplayName
            };
            if (workflowData.Type) {
                createPayload.Type = workflowData.Type;
            }
            if (finalConfig && finalConfig.trim() !== '' && finalConfig.trim() !== '{}') {
                try {
                    const configObj = JSON.parse(finalConfig);
                    if ((configObj.steps && Array.isArray(configObj.steps) && configObj.steps.length > 0) ||
                        (configObj.zSteps && Object.keys(configObj.zSteps).length > 0)) {
                        // Parse Settings for display purposes (more readable)
                        createPayload.Settings = configObj;
                    }
                } catch (e) {
                    // Invalid config
                }
            }

            const createHasChanges = !!(
                workflowData.DisplayName?.trim() ||
                workflowData.Type ||
                (workflowData.Settings && workflowData.Settings.trim() !== '' && workflowData.Settings.trim() !== '{}') ||
                steps.length > 0
            );
            return {
                originalData: null,
                currentData: null,
                expectedPayload: createPayload,
                hasChanges: createHasChanges
            };
        }

        // For edit mode, compute diff data and patch payload

        // Get final config
        let finalConfig = workflowData.Settings || '{}';
        try {
            const configObj = finalConfig.trim() === '' || finalConfig.trim() === '{}'
                ? {}
                : JSON.parse(finalConfig);
            // Ensure structure is correct
            const zSteps: any = configObj.zSteps || {};
            steps
                .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                .forEach(step => {
                    zSteps[step.Id] = {
                        Id: step.Id,
                        DisplayName: step.DisplayName,
                        Description: step.Description,
                        Settings: getStepSettingsValue(step),
                        Script: step.Script,
                        Action: step.Action,
                        Authorization: step.Authorization,
                        EntityType: step.EntityType
                    };
                });
            configObj.zSteps = zSteps;
            const stepsArray = steps
                .filter((step): step is WorkflowStep & { Id: string } => !!step.Id)
                .map(step => ({ stepId: step.Id }));
            configObj.steps = stepsArray;
            finalConfig = JSON.stringify(configObj, null, 2);
        } catch (e) {
            // Invalid config
        }
        const configPayload = getWorkflowConfigPayload(finalConfig);
        // Baseline: use Settings from the full entity we loaded (ref), not the list-item prop which may lack full Settings
        const baselineSettingsStr = (lastLoadedSettingsRef.current != null) ? lastLoadedSettingsRef.current : getWorkflowSettingsString(workflow);
        // Compare in same shape as payload: getWorkflowConfigPayload parses nested zSteps[].Settings to objects,
        // so run baseline through it too. Otherwise API string with nested Settings as strings !== payload with nested Settings as objects.
        const baselinePayload = getWorkflowConfigPayload(baselineSettingsStr);
        const normalizedOldConfig = canonicalJsonString(baselinePayload);
        const normalizedNewConfig = canonicalJsonString(configPayload);
        finalConfig = JSON.stringify(configPayload);

        const operations: any[] = [];
        if (workflow.DisplayName !== workflowData.DisplayName) {
            operations.push({ op: 'replace', path: 'DisplayName', value: workflowData.DisplayName });
        }
        if (workflow.Type !== workflowData.Type) {
            operations.push({ op: 'replace', path: 'Type', value: workflowData.Type });
        }
        if (normalizedOldConfig !== normalizedNewConfig) {
            operations.push({ op: 'replace', path: 'Settings', value: configPayload });
        }

        const patchPayload = operations.length > 0 ? {
            Operations: operations
        } : null;

        // Parse Config JSON strings to objects for better readability in diff
        // Use the already-computed finalConfig (which is a string) and parse it
        let currentConfigObj: any = {};
        let originalConfigObj: any = {};
        try {
            // finalConfig is already a stringified JSON, parse it to object
            if (finalConfig && finalConfig.trim() !== '' && finalConfig.trim() !== '{}') {
                currentConfigObj = JSON.parse(finalConfig);
            } else {
                currentConfigObj = {};
            }
        } catch (e) {
            console.warn('Failed to parse currentConfig for diff:', e);
            currentConfigObj = {};
        }
        try {
            originalConfigObj = baselinePayload && Object.keys(baselinePayload).length > 0 ? baselinePayload : {};
        } catch (e) {
            console.warn('Failed to parse originalConfig for diff:', e);
            originalConfigObj = {};
        }

        // Build current data for comparison (with parsed Config)
        const current = {
            DisplayName: workflowData.DisplayName,
            Type: workflowData.Type,
            Settings: currentConfigObj
        };

        // Build original from same baseline we use for change detection (loaded entity, not list-item prop)
        const original = {
            DisplayName: workflow.DisplayName,
            Type: workflow.Type,
            Settings: originalConfigObj
        };

        // Create display version of patch payload with parsed Settings values
        let displayPayload: any = null;
        if (patchPayload && patchPayload.Operations) {
            displayPayload = {
                Operations: patchPayload.Operations.map((op: any) => {
                    if (op.path === 'Settings' && typeof op.value === 'string') {
                        try {
                            const parsedConfig = JSON.parse(op.value);
                            return { ...op, value: parsedConfig };
                        } catch (e) {
                            return op;
                        }
                    }
                    return op;
                })
            };
        }

        return {
            originalData: original,
            currentData: current,
            expectedPayload: displayPayload || patchPayload || original,
            hasChanges: operations.length > 0
        };
    }, [isEditMode, workflow, workflowData, steps]);

    const {
        unsavedChangesDialogOpen,
        setUnsavedChangesDialogOpen,
        handleCloseAttempt,
        handleConfirmDiscard: handleConfirmDiscardBase
    } = useUnsavedChanges({
        isEditMode,
        formData: workflowData as Record<string, any>,
        originalData: workflow as Record<string, any> | null,
        schemaAttributes: [],
        customHasUnsavedChanges: () => workflowHasChanges
    });

    const handleClose = () => {
        if (!handleCloseAttempt()) return;
        onClose();
    };

    const handleConfirmDiscard = () => {
        handleConfirmDiscardBase(onClose);
    };

    const formContent = (
        <>
            {error && (
                <Box mb="md">
                    <DialogErrorAlert error={error} onClose={() => setError(null)} />
                </Box>
            )}
            {tier3Error && (
                <Box mb="md">
                    <DialogErrorAlert error={tier3Error} onClose={() => setTier3Error(null)} />
                </Box>
            )}

            <Box px={presentation === 'inline' ? 0 : 'md'} pt={presentation === 'inline' ? 0 : 'md'}>
                {isEditMode && !isStandard && (
                    <TextInput
                        label="Id"
                        value={workflow?.Id ?? workflowData.Id ?? ''}
                        readOnly
                        mb="md"
                        w="100%"
                    />
                )}
                <TextInput
                    label="Display Name"
                    value={workflowData.DisplayName || ''}
                    onChange={(e) => setWorkflowData({ ...workflowData, DisplayName: e.currentTarget.value })}
                    onBlur={(e) => {
                        const trimmed = e.currentTarget.value.trim();
                        if (trimmed !== (workflowData.DisplayName || '')) setWorkflowData({ ...workflowData, DisplayName: trimmed });
                    }}
                    required
                    mb="md"
                    w="100%"
                />
                <Select
                    label="Type"
                    value={workflowData.Type || ''}
                    onChange={(value) => setWorkflowData({ ...workflowData, Type: value || '' })}
                    data={[
                        { value: 'Action', label: 'Action' },
                        { value: 'Authorization', label: 'Authorization' },
                        { value: 'Request', label: 'Request' }
                    ]}
                    mb="md"
                    w="100%"
                />
                {!isStandard && tier3Settled && tier3FormAttributes.length > 0 && (
                    <Box mt="md">
                        <Text size="sm" fw={600} c="dimmed" mb="xs">Other attributes (from permissions)</Text>
                        <Stack gap="xs">
                            {tier3FormAttributes.map((attr) => {
                                const key = attr.name || attr.id || '';
                                const value = (workflowData as Record<string, unknown>)[key];
                                const displayValue = value == null ? '' : Array.isArray(value) ? JSON.stringify(value) : String(value);
                                return (
                                    <TextInput
                                        key={key}
                                        label={attr.label || key}
                                        value={displayValue}
                                        onChange={(e) => setWorkflowData({ ...workflowData, [key]: e.currentTarget.value })}
                                        onBlur={(e) => {
                                            const trimmed = e.currentTarget.value.trim();
                                            if (trimmed !== displayValue) setWorkflowData({ ...workflowData, [key]: trimmed });
                                        }}
                                        mb="xs"
                                        w="100%"
                                    />
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Box>

            <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '0')} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                <Tabs.List px={presentation === 'inline' ? 0 : 'md'}>
                    <Tabs.Tab value="0">Steps</Tabs.Tab>
                    {!isStandard && <Tabs.Tab value="1">Configuration</Tabs.Tab>}
                    {!isStandard && isEditMode && <Tabs.Tab value="2">JSON Diff</Tabs.Tab>}
                    {!isStandard && <Tabs.Tab value={isEditMode ? "3" : "2"}>Expected Payload</Tabs.Tab>}
                </Tabs.List>

                <Box p={presentation === 'inline' ? 'sm' : 'md'}>
                    {isEditMode && workflow && isJsonFieldReturnedAsString(workflow.Settings, 'Settings') && (
                        <Alert color="yellow" mb="md">
                            {JSON_FIELD_STRING_WARNING_MESSAGE}
                        </Alert>
                    )}
                    <Tabs.Panel value="0">
                        <Box>
                            <Group justify="space-between" align="center" mb="md">
                                <Text fw={600} size="lg">
                                    Workflow Steps ({steps.length})
                                </Text>
                                <Menu>
                                    <Menu.Target>
                                        <Button variant="outline" leftSection={<IconPlus size={16} />} rightSection={<IconChevronDown size={16} />}>
                                            Add Step
                                        </Button>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <Menu.Item leftSection={<IconPlus size={16} />} onClick={handleCreateNewStep}>
                                            Create New Step
                                        </Menu.Item>
                                        <Menu.Item leftSection={<IconPlus size={16} />} onClick={handleAddExistingStep}>
                                            Add Existing Step
                                        </Menu.Item>
                                    </Menu.Dropdown>
                                </Menu>
                            </Group>

                            {steps.length === 0 ? (
                                <Paper p="lg" ta="center" withBorder>
                                    <Text c="dimmed">
                                        No steps yet. Click "Add Step" to create your first workflow step.
                                    </Text>
                                </Paper>
                            ) : (
                                <Stack gap="xs">
                                    {steps.map((step, index) => (
                                        <Group
                                            key={step.Id || index}
                                            gap="sm"
                                            align="center"
                                            p="sm"
                                            draggable
                                            style={{
                                                borderRadius: 'var(--mantine-radius-sm)',
                                                border: '1px solid var(--mantine-color-default-border)',
                                                backgroundColor: dragOverIndex === index ? 'var(--mantine-color-default-hover)' : undefined,
                                                opacity: draggedIndex === index ? 0.5 : 1,
                                                cursor: 'move',
                                            }}
                                            onDragStart={() => handleDragStart(index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, index)}
                                        >
                                            <IconGripVertical size={20} color="var(--mantine-color-dimmed)" style={{ cursor: 'grab' }} />
                                            <Box flex={1} miw={0}>
                                                <Group gap="xs" align="center">
                                                    <Text size="sm">
                                                        {stepAliases.get(step.Id || '') || step.DisplayName || `Step ${index + 1}`}
                                                    </Text>
                                                    <Badge variant="outline" size="sm" color="blue">
                                                        #{index + 1}
                                                    </Badge>
                                                    {workflowData.Type && (
                                                        (workflowData.Type === 'Action' && (step.Authorization === true || step.Request === true)) ||
                                                        (workflowData.Type === 'Authorization' && (step.Action === true || step.Request === true)) ||
                                                        (workflowData.Type === 'Request' && (step.Action === true || step.Authorization === true))
                                                    ) && (
                                                            <Tooltip label={
                                                                workflowData.Type === 'Action'
                                                                    ? 'This step has Authorization or Request set to true, but the workflow type is Action'
                                                                    : workflowData.Type === 'Authorization'
                                                                        ? 'This step has Action or Request set to true, but the workflow type is Authorization'
                                                                        : 'This step has Action or Authorization set to true, but the workflow type is Request'
                                                            }>
                                                                <IconAlertCircle size={20} color="var(--mantine-color-red-6)" />
                                                            </Tooltip>
                                                        )}
                                                </Group>
                                                <Box>
                                                    {stepAliases.get(step.Id || '') && (
                                                        <Text size="xs" c="dimmed" component="span" display="block">
                                                            {step.DisplayName || `Step ${index + 1}`}
                                                        </Text>
                                                    )}
                                                    <Text size="xs" c="dimmed" component="span" display="block">
                                                        {getStepSettingsValue(step) ? 'Has settings' : 'No settings'}
                                                    </Text>
                                                </Box>
                                            </Box>
                                            <Group gap={4}>
                                                <ActionIcon
                                                    variant="subtle"
                                                    size="sm"
                                                    onClick={() => handleMoveStep(index, 'up')}
                                                    disabled={index === 0}
                                                >
                                                    <IconArrowUp size={16} />
                                                </ActionIcon>
                                                <ActionIcon
                                                    variant="subtle"
                                                    size="sm"
                                                    onClick={() => handleMoveStep(index, 'down')}
                                                    disabled={index === steps.length - 1}
                                                >
                                                    <IconArrowDown size={16} />
                                                </ActionIcon>
                                                <ActionIcon
                                                    variant="subtle"
                                                    size="sm"
                                                    onClick={() => handleEditStep(index)}
                                                >
                                                    <IconPencil size={16} />
                                                </ActionIcon>
                                                <ActionIcon
                                                    variant="subtle"
                                                    size="sm"
                                                    color="red"
                                                    onClick={() => handleDeleteStep(index)}
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </Group>
                                        </Group>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Tabs.Panel>

                    <Tabs.Panel value="1">
                        <ConfigBuilder
                            value={workflowData.Settings || '{}'}
                            onChange={(newSettings: string) => setWorkflowData(prev => ({ ...prev, Settings: newSettings }))}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="2">
                        {isEditMode ? (
                            originalData && currentData ? (
                                <JsonDiffViewer
                                    originalData={originalData}
                                    currentData={currentData}
                                    title="Workflow Changes"
                                    excludedPaths={['Id', 'EntityType', 'Settings.editVersion', 'Settings.editDate']}
                                    collapsible={false}
                                    defaultExpanded={true}
                                />
                            ) : (
                                <Box p="md">
                                    <Text size="sm" c="dimmed">
                                        No changes detected
                                    </Text>
                                </Box>
                            )
                        ) : (
                            <JsonViewer
                                data={expectedPayload}
                                title="Expected CREATE Payload"
                                description="This is the CREATE request that will be sent to create the workflow."
                                formatBodyMultiline
                            />
                        )}
                    </Tabs.Panel>

                    <Tabs.Panel value="3">
                        {isEditMode && (
                            <JsonViewer
                                data={expectedPayload}
                                title="Expected PATCH Payload"
                                description="This is the PATCH request that will be sent to update the workflow."
                                formatBodyMultiline
                            />
                        )}
                    </Tabs.Panel>
                </Box>
            </Tabs>

            <Box pt="md" mt="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                <EntityDialogActions
                    onCancel={handleClose}
                    onSave={handleSave}
                    loading={loading}
                    isEditMode={isEditMode}
                    hasChanges={workflowHasChanges}
                    isValid={!!workflowData.DisplayName}
                    saveLabel="Update"
                    createLabel="Create"
                    showApply={false}
                />
            </Box>
        </>
    );

    const childDialogs = (
        <>
            <ShadcnUnsavedChangesDialog
                open={unsavedChangesDialogOpen}
                onClose={() => setUnsavedChangesDialogOpen(false)}
                onConfirm={handleConfirmDiscard}
                mode={isEditMode ? 'edit' : 'create'}
                entityType="Workflow"
            />

            <WorkflowStepEditDialog
                open={stepEditDialogOpen}
                onClose={() => {
                    setStepEditDialogOpen(false);
                    setEditingStep(null);
                    setEditingStepIndex(null);
                }}
                onSuccess={handleStepSave}
                step={editingStep}
                entityType={stepEntityType}
                storedCopy={editingStep && '_storedCopy' in editingStep ? editingStep._storedCopy : null}
            />

            <EntityBrowserDialog
                open={entityBrowserOpen}
                onClose={() => setEntityBrowserOpen(false)}
                onConfirm={handleExistingStepSelected}
                entityType={stepEntityType}
                title="Select Workflow Activity Steps"
                callerName="WorkflowDesigner"
                singleSelection={false}
                scimFilter={workflowData.Type ? `${workflowData.Type} eq true` : undefined}
            />
        </>
    );

    if (presentation === 'inline') {
        if (!open) return null;
        return (
            <>
                <Box>{formContent}</Box>
                {childDialogs}
            </>
        );
    }

    return (
        <>
            <Modal
                opened={open}
                onClose={handleClose}
                title={isEditMode ? 'Edit Workflow' : 'Create Workflow'}
                size={MODAL_SIZES.detail}
                styles={{
                    content: { height: '90vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }
                }}
            >
                {formContent}
            </Modal>
            {childDialogs}
        </>
    );
};

